import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Adds a suggested product from the cart drawer without leaving the page.
 * The forms post to /cart/add natively when this module is unavailable.
 * After a successful add the drawer re-renders and the suggestion disappears.
 */
class CartCompleteLook extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.addEventListener('submit', this.#onSubmit, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();

    const id = Number(new FormData(form).get('id'));
    if (!id) return;

    const button = form.querySelector('button[type="submit"]');
    const error = form.querySelector('[data-cart-look-error]');
    if (button instanceof HTMLButtonElement) {
      if (button.disabled) return;
      button.disabled = true;
    }
    if (error instanceof HTMLElement) error.hidden = true;

    const root = window.Shopify?.routes?.root || '/';
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      const ajaxCart = await fetch(`${root}cart.js`).then((r) => r.json());
      const failed = !response.ok || Boolean(data.status);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'cart-complete-look',
          sourceId: 'cart-complete-look',
          itemCount: 1,
          sections: failed ? undefined : data.sections,
          didError: failed,
        },
      });

      if (failed) this.#showError(error, button, data.description || data.message);
    } catch (err) {
      deferred.reject(err);
      this.#showError(error, button);
    }
  };

  /**
   * @param {Element | null} error
   * @param {Element | null} button
   * @param {string} [message]
   */
  #showError(error, button, message) {
    if (error instanceof HTMLElement) {
      error.textContent = message || this.dataset.errorText || '';
      error.hidden = false;
    }
    if (button instanceof HTMLButtonElement) button.disabled = false;
  }
}

if (!customElements.get('cart-complete-look')) {
  customElements.define('cart-complete-look', CartCompleteLook);
}
